/** @jsxImportSource @emotion/react */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Fab, Zoom } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import useChatbot from '../../hooks/useChatbot';
import { useChatbotStore } from '../../stores/useChatbotStore';

const MIN_WIDTH = 380;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 560;

/**
 * AI 어시스턴트 패널 — **App에서 하나만 렌더한다.**
 *
 * 화면마다 심으면 페이지가 언마운트될 때 대화가 사라지므로, 열림 여부와 화면 맥락을
 * useChatbotStore에서 읽는다. 각 화면은 openPanel(pageContext)만 호출하면 된다.
 */
export default function ChatbotPanel() {
  const open = useChatbotStore((s) => s.open);
  const pageContext = useChatbotStore((s) => s.pageContext);
  const sessionId = useChatbotStore((s) => s.sessionId);
  const closePanel = useChatbotStore((s) => s.closePanel);
  const { messages, isLoading, sendMessage, stopStreaming, clearMessages, initSession } =
    useChatbot();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_WIDTH);
  const isResizing = useRef(false);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  // 세션이 없을 때만 발급한다 — 이미 대화 중이면 맥락을 유지해야 하므로 재발급하지 않는다
  useEffect(() => {
    if (open && !sessionId) {
      initSession(pageContext);
    }
  }, [open, sessionId, initSession, pageContext]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distFromBottom > 100);
  };

  // 패널 너비 드래그 조절
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizing.current = true;
      const startX = e.clientX;
      const startWidth = drawerWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isResizing.current) return;
        const delta = startX - moveEvent.clientX;
        const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
        setDrawerWidth(newWidth);
      };

      const handleMouseUp = () => {
        isResizing.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [drawerWidth]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={closePanel}
      variant="persistent"
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          top: 48,
          height: 'calc(100vh - 48px)',
          borderLeft: '1px solid #e0e0e0',
          boxShadow: '-2px 0 8px rgba(0,0,0,0.06)',
          overflow: 'visible',
        },
      }}
    >
      {/* 리사이즈 핸들 */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          left: -4,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'col-resize',
          zIndex: 10,
          '&:hover': { bgcolor: 'rgba(45,155,178,0.15)' },
        }}
      />

      {/* 헤더 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #e8ecf0',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 35,
              height: 35,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <img
              src="/assets/icon/chatbot-icon.png"
              alt=""
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a' }}>
              AI 어시스턴트
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#999' }}>
              설비 데이터 분석 지원
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={clearMessages}
            title="대화 초기화"
            sx={{ color: '#999', '&:hover': { color: '#666', bgcolor: '#f5f5f5' } }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={closePanel}
            sx={{ color: '#999', '&:hover': { color: '#666', bgcolor: '#f5f5f5' } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      {/* 메시지 영역 */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 2,
          bgcolor: '#fff',
          position: 'relative',
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#d0d0d0', borderRadius: 3 },
        }}
      >
        {messages
          .filter((msg) => !(msg.isStreaming && msg.content === ''))
          .map((msg) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              onSendExample={sendMessage}
              onSelectMatrix={(text) => sendMessage(text, true)}
            />
          ))}

        {/* Typing indicator */}
        {isLoading &&
          messages.length > 0 &&
          messages[messages.length - 1].content === '' &&
          messages[messages.length - 1].isStreaming && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', px: 2, py: 0.8 }}>
              <Box sx={{ maxWidth: '85%' }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#999', fontSize: '0.7rem', mb: 0.3, display: 'block' }}
                >
                  AI 어시스턴트
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#f0f4f8',
                    px: 2,
                    py: 1.5,
                    borderRadius: '0 16px 16px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '4px',
                      alignItems: 'center',
                      '& span': {
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#2D9BB2',
                        display: 'inline-block',
                        animation: 'dotPulse 1.4s infinite',
                      },
                      '& span:nth-of-type(2)': { animationDelay: '0.2s' },
                      '& span:nth-of-type(3)': { animationDelay: '0.4s' },
                      '@keyframes dotPulse': {
                        '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                        '40%': { opacity: 1, transform: 'scale(1)' },
                      },
                    }}
                  >
                    <span />
                    <span />
                    <span />
                  </Box>
                  <Typography sx={{ fontSize: '0.8rem', color: '#999', ml: 0.5 }}>
                    분석 중...
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
      </Box>

      {/* 스크롤 하단 버튼 */}
      <Zoom in={showScrollBtn}>
        <Fab
          size="small"
          onClick={scrollToBottom}
          sx={{
            position: 'absolute',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: '#fff',
            color: '#666',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            width: 32,
            height: 32,
            minHeight: 32,
            '&:hover': { bgcolor: '#f5f5f5' },
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
        </Fab>
      </Zoom>

      {/* 입력 영역 */}
      <ChatInput onSend={sendMessage} onStop={stopStreaming} isLoading={isLoading} />
    </Drawer>
  );
}
